import type { components } from '@/api/schema'
import { parseUTCDate } from '@/lib/utils'

type ShipmentRevision = components['schemas']['ShipmentRevisionRead']

const KIND_LABELS: Record<string, string> = {
  dimensions: 'Dimensions',
  weight: 'Weight',
  freight_class: 'Freight class',
  pallet_count: 'Pallet count',
  accessorial: 'Accessorial',
  charge: 'Extra charge',
  delivery_date: 'Delivery date',
  other: 'Other',
}

function shortDay(value: string) {
  const day = parseUTCDate(value)
  return day.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(day.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  })
}

/** A carrier delivery date is stored as an ISO day (USA-1717); every other value is shown as reported. */
function shown(revision: ShipmentRevision, value: string) {
  if (revision.kind !== 'delivery_date' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  return new Date(`${value}T12:00:00`).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * What paperwork, a reweigh or the carrier reported after booking (USA-1697). The booking above is
 * what we paid for and stays as booked; each line shows the booked value struck through, what was
 * reported, and where it came from. Nothing renders when nothing was reported.
 */
export function ShipmentRevisions({ revisions }: { revisions: ShipmentRevision[] | undefined }) {
  if (!revisions?.length) return null
  return (
    <div>
      <p className="mb-1 text-xs font-medium text-muted-foreground">After booking</p>
      <ul className="space-y-1">
        {revisions.map((revision) => (
          <li
            key={revision.id}
            className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm"
            title={revision.note ?? undefined}
          >
            <span className="font-medium">{KIND_LABELS[revision.kind] ?? revision.kind}</span>
            {revision.booked_value && revision.booked_value !== revision.reported_value && (
              <>
                <span className="text-muted-foreground line-through">
                  {shown(revision, revision.booked_value)}
                </span>
                <span className="text-muted-foreground" aria-label="reported as">
                  →
                </span>
              </>
            )}
            <span>{shown(revision, revision.reported_value)}</span>
            <span className="text-xs text-muted-foreground">
              {revision.source} · {shortDay(revision.created_at)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
